import { useState, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatCents } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { SeatMapRenderer } from '@/components/seatmap/SeatMapRenderer';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Empty } from '@/components/ui/Empty';
import { toast } from '@/lib/toast';
import {
  useCreateTicketHold,
  useReleaseTicketHold,
  useSeatAvailability,
  useCreateCheckoutSession,
  useAutoHoldSeats,
  useShowSaleStatus,
} from '@/data/stripe';

export const Route = createFileRoute('/parent/shows/$showId/seats')({
  component: SeatPurchase,
});

interface Section {
  id: string;
  name: string;
  row_count: number;
  seat_count_per_row: number;
  pricing_cents: number;
  display_order: number;
  production_id: string;
}

interface Seat {
  id: string;
  section_id: string;
  row_label: string;
  seat_number: number;
  blocked: boolean;
}

interface ShowDetail {
  id: string;
  name: string;
  performance_at: string;
  production: { id: string; name: string };
}

function useShowDetail(showId: string) {
  return useQuery({
    queryKey: ['show', showId],
    queryFn: async (): Promise<ShowDetail | null> => {
      const { data, error } = await supabase
        .from('shows')
        .select('id, name, performance_at, productions:production_id(id, name)')
        .eq('id', showId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const row = data;
      return {
        id: row.id,
        name: row.name,
        performance_at: row.performance_at,
        production: row.productions,
      };
    },
  });
}

function useShowSeatMap(productionId: string | undefined) {
  return useQuery({
    enabled: !!productionId,
    queryKey: ['seat-map', productionId],
    queryFn: async () => {
      const { data: sections, error: e1 } = await supabase
        .from('seat_map_sections')
        .select('*')
        .eq('production_id', productionId!)
        .order('display_order');
      if (e1) throw e1;
      const sectionRows = (sections ?? []) as Section[];
      if (sectionRows.length === 0) return { sections: [], seats: [] };
      const { data: seats, error: e2 } = await supabase
        .from('seats')
        .select('*')
        .in(
          'section_id',
          sectionRows.map((s) => s.id),
        );
      if (e2) throw e2;
      return { sections: sectionRows, seats: seats ?? [] };
    },
  });
}

const HOLD_MINUTES = 15;

function SeatPurchase() {
  const { showId } = Route.useParams();
  const navigate = useNavigate();
  const show = useShowDetail(showId);
  const seatMap = useShowSeatMap(show.data?.production.id);
  const availability = useSeatAvailability(showId);
  const saleStatus = useShowSaleStatus(showId);
  const createHold = useCreateTicketHold();
  const releaseHold = useReleaseTicketHold();
  const autoHold = useAutoHoldSeats();
  const checkout = useCreateCheckoutSession();

  // selectedSeatId → hold_id mapping. We create a hold immediately on click so
  // another shopper can't take the seat while this user is deciding.
  const [holds, setHolds] = useState<Record<string, string>>({});
  // Sprint 11 — auto-pick quantity. Defaults to 2 (most common ask).
  const [autoQty, setAutoQty] = useState<number>(2);

  const soldSet = useMemo(
    () => new Set(availability.data?.sold_seat_ids ?? []),
    [availability.data?.sold_seat_ids],
  );
  const heldByOthers = useMemo(() => {
    const otherSet = new Set(availability.data?.held_seat_ids ?? []);
    // Remove our own holds — they should appear as 'selected', not 'sold'.
    for (const seatId of Object.keys(holds)) otherSet.delete(seatId);
    return otherSet;
  }, [availability.data?.held_seat_ids, holds]);
  const selectedSet = useMemo(() => new Set(Object.keys(holds)), [holds]);

  const sections = useMemo(() => seatMap.data?.sections ?? [], [seatMap.data?.sections]);
  const seats = useMemo(() => seatMap.data?.seats ?? [], [seatMap.data?.seats]);

  // Compute the running total from selected seats' section prices.
  const total = useMemo(() => {
    let cents = 0;
    for (const seatId of selectedSet) {
      const seat = seats.find((s: Seat) => s.id === seatId);
      if (!seat) continue;
      const section = sections.find((sec: Section) => sec.id === seat.section_id);
      if (section) cents += section.pricing_cents;
    }
    return cents;
  }, [selectedSet, seats, sections]);

  // The renderer paints sold + other-held as "sold" (unavailable). Our own
  // holds appear "selected".
  const unavailable = useMemo(() => {
    const u = new Set(soldSet);
    for (const id of heldByOthers) u.add(id);
    return u;
  }, [soldSet, heldByOthers]);

  const cap = saleStatus.data?.max_tickets_per_purchase ?? null;
  const onSale = saleStatus.data?.on_sale ?? true;
  const heldCount = Object.keys(holds).length;
  // Sprint 11: refuse to hold beyond the cap. The server enforces the same
  // boundary at checkout time, but client-side prevents the failure mode
  // where the parent picks 8 seats and only discovers the limit at the
  // checkout button.
  const wouldExceedCap = cap !== null && heldCount >= cap;

  async function onSelectSeat(seatId: string) {
    if (soldSet.has(seatId) || heldByOthers.has(seatId)) return;
    if (!onSale) {
      toast({
        title: 'Sales paused',
        description: 'Ticket sales for this show are not currently open.',
        variant: 'error',
      });
      return;
    }
    if (holds[seatId]) {
      // Toggle off — release the hold.
      const hid = holds[seatId];
      try {
        await releaseHold.mutateAsync({ hold_id: hid, show_id: showId });
        setHolds((prev) => {
          const next = { ...prev };
          delete next[seatId];
          return next;
        });
      } catch (err) {
        toast({
          title: 'Could not release seat',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'error',
        });
      }
      return;
    }
    if (wouldExceedCap) {
      toast({
        title: 'Cap reached',
        description: `This show limits each purchase to ${cap} seats. Release one to swap.`,
        variant: 'error',
      });
      return;
    }
    try {
      const hid = await createHold.mutateAsync({
        seat_id: seatId,
        show_id: showId,
        hold_minutes: HOLD_MINUTES,
      });
      setHolds((prev) => ({ ...prev, [seatId]: hid }));
    } catch (err) {
      toast({
        title: 'Could not hold seat',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  async function onAutoPick() {
    if (!onSale) {
      toast({ title: 'Sales paused', variant: 'error' });
      return;
    }
    if (cap !== null && autoQty + heldCount > cap) {
      toast({
        title: 'Would exceed cap',
        description: `Asking for ${autoQty} would put you over the ${cap}-per-purchase limit.`,
        variant: 'error',
      });
      return;
    }
    try {
      const picked = await autoHold.mutateAsync({
        show_id: showId,
        quantity: autoQty,
        hold_minutes: HOLD_MINUTES,
      });
      setHolds((prev) => {
        const next = { ...prev };
        for (const r of picked) next[r.seat_id] = r.hold_id;
        return next;
      });
      toast({
        title: `Picked ${picked.length} seat${picked.length === 1 ? '' : 's'}`,
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: "Couldn't auto-pick",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  async function onCheckout() {
    const holdIds = Object.values(holds);
    if (holdIds.length === 0) return;
    try {
      const successUrl = `${window.location.origin}/parent/billing/checkout-return?mode=tickets&status=success`;
      const cancelUrl = `${window.location.origin}/parent/shows/${showId}/seats`;
      const resp = await checkout.mutateAsync({
        mode: 'tickets',
        hold_ids: holdIds,
        success_url: successUrl,
        cancel_url: cancelUrl,
      });
      window.location.href = resp.url;
    } catch (err) {
      toast({
        title: "Couldn't start checkout",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  if (show.isLoading) return <p aria-live="polite">Loading…</p>;
  if (!show.data) {
    return (
      <Empty
        title="Show not found"
        description="It may have been removed or the link is incorrect."
      />
    );
  }

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          {show.data.production.name}
        </p>
        <h1 className="mt-2">{show.data.name} — tickets</h1>
        <p className="mt-2 text-[var(--color-ink-muted)]">
          {new Date(show.data.performance_at).toLocaleString()}
        </p>
        <p className="mt-4 max-w-prose text-[var(--color-ink-subtle)] text-[var(--text-sm)]">
          Select seats below or use <strong>Auto-pick</strong> to let the system find adjacent seats
          for you. Each selection holds the seat for {HOLD_MINUTES} minutes. Click{' '}
          <strong>Check out</strong> to pay with Stripe — your seats are released if you cancel or
          the hold expires.
          {cap !== null && (
            <>
              {' '}
              This show limits each purchase to <strong>{cap}</strong> tickets.
            </>
          )}
        </p>
      </header>

      {/* Sales-status banner — paused / scheduled / seats-left visibility. */}
      {saleStatus.data && !onSale && (
        <div role="status" className="surface border-l-4 border-[var(--color-warn)] p-4">
          <p className="font-medium">Ticket sales are not currently open.</p>
          {saleStatus.data.ticket_sales_open_at && (
            <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
              Sales open {new Date(saleStatus.data.ticket_sales_open_at).toLocaleString()}.
            </p>
          )}
        </div>
      )}
      {saleStatus.data && onSale && saleStatus.data.seats_available < 20 && (
        <div
          role="status"
          className="surface border-l-4 border-[var(--color-warn)] p-4 text-[var(--text-sm)]"
        >
          Only <strong>{saleStatus.data.seats_available}</strong> seat
          {saleStatus.data.seats_available === 1 ? '' : 's'} left.
        </div>
      )}

      {/* Auto-pick controls — Ticketmaster-style "buy N adjacent seats". */}
      {sections.length > 0 && onSale && (
        <section
          aria-labelledby="auto-pick"
          className="surface-elevated flex flex-wrap items-end gap-3 p-4"
        >
          <h2
            id="auto-pick"
            className="font-medium tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase"
          >
            Auto-pick
          </h2>
          <div>
            <label
              htmlFor="auto-qty"
              className="block text-[var(--color-ink-muted)] text-[var(--text-xs)]"
            >
              How many?
            </label>
            <Input
              id="auto-qty"
              type="number"
              min="1"
              max={Math.min(cap ?? 20, 20)}
              value={autoQty}
              onChange={(e) => setAutoQty(Math.max(1, Number.parseInt(e.target.value, 10) || 1))}
              className="w-24"
            />
          </div>
          <Button
            variant="secondary"
            onClick={() => void onAutoPick()}
            disabled={autoHold.isPending || (cap !== null && autoQty + heldCount > cap)}
          >
            {autoHold.isPending ? 'Finding seats…' : `Pick ${autoQty} adjacent`}
          </Button>
          <p className="text-[var(--color-ink-subtle)] text-[var(--text-xs)]">
            We'll find the best available row of {autoQty} together. You can still swap any seat
            manually.
          </p>
        </section>
      )}

      {seatMap.isLoading && <p aria-live="polite">Loading seat map…</p>}

      {sections.length === 0 && !seatMap.isLoading && (
        <Empty
          title="No seats configured"
          description="The studio hasn't published the seat map for this show yet."
        />
      )}

      {sections.length > 0 && (
        <>
          <SeatMapRenderer
            sections={sections}
            seats={seats}
            soldSeatIds={unavailable}
            selectedSeatIds={selectedSet}
            onSelectSeat={(id) => void onSelectSeat(id)}
          />

          <aside
            aria-labelledby="checkout-summary"
            className="surface-elevated sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-4 p-5"
          >
            <h2 id="checkout-summary" className="sr-only">
              Checkout summary
            </h2>
            <div>
              <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-xs)] uppercase">
                {selectedSet.size === 1 ? 'Seat selected' : 'Seats selected'}
                {cap !== null && (
                  <span className="ml-1">
                    ({heldCount}/{cap})
                  </span>
                )}
              </p>
              <p className="font-[var(--font-display)] text-[var(--text-2xl)] tabular-nums">
                {selectedSet.size} · {formatCents(total)}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={() => void navigate({ to: '/parent/billing/tickets' })}
              >
                View my tickets
              </Button>
              <Button
                onClick={() => void onCheckout()}
                disabled={selectedSet.size === 0 || checkout.isPending || !onSale}
              >
                {checkout.isPending ? 'Starting checkout…' : 'Check out'}
              </Button>
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
